import { access } from 'fs';
import React, { useState, useEffect } from 'react';

interface InstagramCommentReply {
  id: string;
  text: string;
  username?: string;
  timestamp: string;
  isGenerated?: boolean;
  profilePicture?: string | null;
  userId?: string | null;
}

interface InstagramComment {
  id: string;
  text: string;
  username?: string;
  timestamp: string;
  isGenerated?: boolean;
  profilePicture?: string | null;
  userId?: string | null;
  replies?: InstagramCommentReply[];
  replies_count?: number;
}

interface InstagramPost {
  id: string;
  media_url: string;
  permalink: string;
  caption?: string;
  media_type: 'IMAGE' | 'VIDEO' | 'CAROUSEL_ALBUM';
  timestamp: string;
  thumbnail_url?: string;
  comments?: InstagramComment[];
  comments_count?: number;
  like_count?: number;
}

interface KidsaInstagramProps {
  title?: string;
  instagram?: string; 
  accessToken?: string; 
  facebookPageId?: string; 
  maxPosts?: number;
  // 🔄 NUEVAS PROPS PARA AUTO-RENOVACIÓN
  appId?: string;
  appSecret?: string;
  enableTokenRefresh?: boolean;
}

const KidsaInstagram: React.FC<KidsaInstagramProps> = ({
  title,
  instagram,
  accessToken,
  facebookPageId,
  maxPosts,
  appId,
  appSecret,
  enableTokenRefresh = true
}) => {
  // Estados originales
  const [posts, setPosts] = useState<InstagramPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState<InstagramPost | null>(null);
  const [loadingComments, setLoadingComments] = useState(false);
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set());
  
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null);

  // 🔄 NUEVOS ESTADOS PARA MANEJO DE TOKENS
  const [currentToken, setCurrentToken] = useState(accessToken);
  const [tokenStatus, setTokenStatus] = useState<'checking' | 'valid' | 'invalid' | 'refreshed' | 'refresh_failed' | 'error'>('valid');
  const [tokenExpiryDate, setTokenExpiryDate] = useState<Date | null>(null);
  const [tokenCheckInterval, setTokenCheckInterval] = useState<NodeJS.Timeout | null>(null);

  const PAGE_ACCESS_TOKEN = currentToken; // Usar el token actual (puede ser renovado)
  const FACEBOOK_PAGE_ID = facebookPageId;

  // 🔄 FUNCIONES PARA MANEJO DE TOKENS
  const getTokenInfo = async (token: string) => {
    if (!appId || !appSecret || !enableTokenRefresh) return null;
    
    try {
      const response = await fetch(
        `https://graph.facebook.com/v18.0/debug_token?` +
        `input_token=${token}&` +
        `access_token=${appId}|${appSecret}`
      );

      const data = await response.json();
      
      if (data.data) {
        const tokenData = data.data;
        return {
          isValid: tokenData.is_valid,
          expiresAt: tokenData.expires_at ? new Date(tokenData.expires_at * 1000) : null,
          scopes: tokenData.scopes,
          userId: tokenData.user_id,
          appId: tokenData.app_id
        };
      }
      return null;
    } catch (error) {
      console.error('❌ Error obteniendo info del token:', error);
      return null;
    }
  };

  const refreshToken = async (token: string) => {
    if (!appId || !appSecret || !enableTokenRefresh) {
      throw new Error('App ID y App Secret son requeridos para renovar el token');
    }
    
    try {
      const response = await fetch(
        `https://graph.facebook.com/v18.0/oauth/access_token?` +
        `grant_type=fb_exchange_token&` +
        `client_id=${appId}&` +
        `client_secret=${appSecret}&` +
        `fb_exchange_token=${token}`
      );

      const data = await response.json();
      
      if (data.access_token) {
        const expiresAt = new Date(Date.now() + (data.expires_in * 1000));
        console.log('✅ Token renovado exitosamente');
        console.log('📅 Nuevo token expira:', expiresAt);
        console.log('🔑 Nuevo token:', data.access_token);
        
        return {
          token: data.access_token,
          expiresIn: data.expires_in,
          expiresAt: expiresAt
        };
      } else {
        throw new Error(data.error?.message || 'No se pudo renovar el token');
      }
    } catch (error) {
      console.error('❌ Error renovando token:', error);
      throw error;
    }
  };

  const isTokenExpiringSoon = (expiresAt: Date | null, daysBeforeExpiry = 7) => {
    if (!expiresAt) return false;
    const now = new Date();
    const daysUntilExpiry = (expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
    return daysUntilExpiry <= daysBeforeExpiry;
  };

  const checkAndRefreshToken = async () => {
    if (!enableTokenRefresh || !appId || !appSecret || !currentToken) return;

    try {
      setTokenStatus('checking');
      const tokenInfo = await getTokenInfo(currentToken);
      
      if (!tokenInfo || !tokenInfo.isValid) {
        setTokenStatus('invalid');
        setError('Token inválido. Necesita renovación manual.');
        return;
      }

      setTokenExpiryDate(tokenInfo.expiresAt);

      if (isTokenExpiringSoon(tokenInfo.expiresAt, 7)) {
        console.log('⚠️ Token expira pronto, renovando automáticamente...');
        try {
          const newTokenData = await refreshToken(currentToken);
          setCurrentToken(newTokenData.token);
          setTokenExpiryDate(newTokenData.expiresAt);
          setTokenStatus('refreshed');
          setError(null);
          
          setTimeout(() => {
            setTokenStatus('valid');
          }, 5000);
          
        } catch (refreshError) {
          console.error('Error renovando token:', refreshError);
          setTokenStatus('refresh_failed');
          setError('No se pudo renovar el token automáticamente.');
        }
      } else {
        setTokenStatus('valid');
        if (tokenInfo.expiresAt) {
          const daysLeft = Math.ceil((tokenInfo.expiresAt.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
          console.log(`✅ Token válido por ${daysLeft} días más`);
        }
      }
    } catch (error) {
      console.error('Error verificando token:', error);
      setTokenStatus('error');
      setError('Error verificando el estado del token.');
    }
  };

  const handleManualRefresh = async () => {
    if (!enableTokenRefresh || !appId || !appSecret) {
      setError('Renovación de token no está habilitada o faltan credenciales.');
      return;
    }

    if (!accessToken) {
      setError('No hay token de acceso para renovar.');
      return;
    }

    try {
      console.log('🔄 Renovando token manualmente...');
      setTokenStatus('checking');
      const newTokenData = await refreshToken(accessToken);
      setCurrentToken(newTokenData.token);
      setTokenExpiryDate(newTokenData.expiresAt);
      setTokenStatus('refreshed');
      setError(null);
      
      setTimeout(() => {
        setTokenStatus('valid');
      }, 3000);
      
    } catch (error) {
      console.error('Error en renovación manual:', error);
      setTokenStatus('refresh_failed');
      setError(error instanceof Error ? error.message : 'No se pudo renovar el token manualmente.');
    }
  };

  // 🔄 VERIFICACIÓN INICIAL Y PERIÓDICA DEL TOKEN
  useEffect(() => {
    if (enableTokenRefresh && appId && appSecret) {
      checkAndRefreshToken();
      
      const interval = setInterval(() => {
        checkAndRefreshToken();
      }, 24 * 60 * 60 * 1000); // Cada 24 horas
      
      setTokenCheckInterval(interval);
      
      return () => {
        if (interval) clearInterval(interval);
      };
    }
  }, [currentToken, enableTokenRefresh, appId, appSecret]);

  useEffect(() => {
    return () => {
      if (tokenCheckInterval) {
        clearInterval(tokenCheckInterval);
      }
    };
  }, [tokenCheckInterval]);

  // TUS FUNCIONES ORIGINALES (con mejora para manejar token expirado)
  const fetchInstagramPosts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const pageResponse = await fetch(
        `https://graph.facebook.com/v18.0/${FACEBOOK_PAGE_ID}?fields=instagram_business_account&access_token=${PAGE_ACCESS_TOKEN}`
      );

      if (!pageResponse.ok) {
        const errorData = await pageResponse.json();
        
        // 🔄 SI EL TOKEN EXPIRÓ, INTENTAR RENOVARLO AUTOMÁTICAMENTE
        if (errorData.error.code === 190 && enableTokenRefresh && appId && appSecret && currentToken) {
          console.log('🔄 Token expirado detectado, intentando renovar...');
          try {
            const newTokenData = await refreshToken(currentToken);
            setCurrentToken(newTokenData.token);
            setTokenExpiryDate(newTokenData.expiresAt);
            setTokenStatus('refreshed');
            // Reintentar con el nuevo token
            return fetchInstagramPosts();
          } catch (refreshError) {
            throw new Error('Token expirado y no se pudo renovar automáticamente. Configura appId y appSecret para auto-renovación.');
          }
        }
        
        throw new Error(`Error al obtener la cuenta de Instagram: ${errorData.error.message}`);
      }

      const pageData = await pageResponse.json();
      
      if (!pageData.instagram_business_account?.id) {
        throw new Error('No se encontró cuenta de Instagram Business vinculada.');
      }

      const instagramAccountId = pageData.instagram_business_account.id;

      const postsResponse = await fetch(
        `https://graph.facebook.com/v18.0/${instagramAccountId}/media?fields=id,media_url,permalink,caption,media_type,timestamp,thumbnail_url,comments_count,like_count&limit=${maxPosts}&access_token=${PAGE_ACCESS_TOKEN}`
      );

      if (!postsResponse.ok) {
        const errorData = await postsResponse.json();
        throw new Error(`Error al obtener posts: ${errorData.error.message}`);
      }

      const postsData = await postsResponse.json();
      const allPosts = postsData.data || [];
      
      console.log('📊 INFORMACIÓN DETALLADA DE POSTS:');
      allPosts.forEach((post, index) => {
        console.log(`📝 Post ${index + 1}:`, {
          id: post.id,
          caption: post.caption?.substring(0, 50) + '...',
          comments_count: post.comments_count,
          like_count: post.like_count,
          media_type: post.media_type,
          permalink: post.permalink
        });
      });
      
      console.log('Total posts obtenidos:', allPosts.length);

      setPosts(allPosts);
    } catch (err) {
      console.error('Error fetching Instagram posts:', err);
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  };

  const fetchCommentReplies = async (commentId: string): Promise<InstagramCommentReply[]> => {
    try {
      console.log(`🔗 Obteniendo respuestas para comentario ID: ${commentId}`);
      
      const response = await fetch(
        `https://graph.facebook.com/v18.0/${commentId}/replies?fields=id,text,timestamp,username&access_token=${PAGE_ACCESS_TOKEN}`
      );
      
      if (response.ok) {
        const data = await response.json();
        const repliesCount = data.data?.length || 0;
        
        console.log(`💬 Respuestas encontradas: ${repliesCount}`);
        
        if (data.data && data.data.length > 0) {
          const processedReplies = data.data.map((reply: any, index: number) => {
            let username = reply.username || `Usuario_${index + 1}`;
            let isReal = !!reply.username;
            
            return {
              id: reply.id,
              text: reply.text || '',
              timestamp: reply.timestamp,
              username: username,
              isGenerated: !isReal,
              profilePicture: null,
              userId: null
            };
          });
          
          return processedReplies;
        }
      } else {
        const basicResponse = await fetch(
          `https://graph.facebook.com/v18.0/${commentId}/replies?fields=id,text,timestamp&access_token=${PAGE_ACCESS_TOKEN}`
        );
        
        if (basicResponse.ok) {
          const basicData = await basicResponse.json();
          
          const basicReplies = (basicData.data || []).map((reply: any, index: number) => {
            const friendlyNames = [
              'Nicole Muñoz', 'Jorge Carrasco', 'Charice Lantzy', 'Héctor Villarroel', 'Yosko Carrasco', 
              'Franco Villarroel', 'Niko Carrasco', 'Santiago Villarroel', 'Patricia Yin', 'Matilde Villarroel', 'Benja Villarroel'
            ];
            
            return {
              id: reply.id,
              text: reply.text || '',
              timestamp: reply.timestamp,
              username: friendlyNames[index % friendlyNames.length],
              isGenerated: true,
              profilePicture: null,
              userId: null
            };
          });
          
          return basicReplies;
        }
      }
      
      return [];
    } catch (error) {
      console.error('💥 Error al obtener respuestas:', error);
      return [];
    }
  };

  const fetchPostComments = async (postId: string, isRefresh = false) => {
    try {
      if (!isRefresh) {
        setLoadingComments(true);
      }
      
      console.log(`🚀 ${isRefresh ? 'Actualizando' : 'Obteniendo'} comentarios para post ID:`, postId);
      
      const response = await fetch(
        `https://graph.facebook.com/v18.0/${postId}/comments?fields=id,text,timestamp,username&access_token=${PAGE_ACCESS_TOKEN}`
      );
      
      if (response.ok) {
        const data = await response.json();
        const commentCount = data.data?.length || 0;
        
        console.log(`${isRefresh ? '🔄' : '✅'} Comentarios encontrados:`, commentCount);
        
        if (data.data && data.data.length > 0) {
          const processedComments = await Promise.all(
            data.data.map(async (comment: any, index: number) => {
              let username = comment.username || `Usuario_${index + 1}`;
              let isReal = !!comment.username;
              
              const replies = await fetchCommentReplies(comment.id);
              
              return {
                id: comment.id,
                text: comment.text || '',
                timestamp: comment.timestamp,
                username: username,
                isGenerated: !isReal,
                profilePicture: null,
                userId: null,
                replies: replies,
                replies_count: replies.length
              };
            })
          );
          
          setLastUpdate(new Date());
          
          if (isRefresh) {
            console.log('🔄 Comentarios y respuestas actualizados automáticamente');
          }
          
          return processedComments;
          
        } else {
          return [];
        }
        
      } else {
        const basicResponse = await fetch(
          `https://graph.facebook.com/v18.0/${postId}/comments?fields=id,text,timestamp&access_token=${PAGE_ACCESS_TOKEN}`
        );
        
        if (basicResponse.ok) {
          const basicData = await basicResponse.json();
          
          const basicComments = await Promise.all(
            (basicData.data || []).map(async (comment: any, index: number) => {
              const friendlyNames = [
                'Nicole Muñoz', 'Jorge Carrasco', 'Charice Lantzy', 'Héctor Villarroel', 'Yosko Carrasco', 
                'Franco Villarroel', 'Niko Carrasco', 'Santiago Villarroel', 'Patricia Yin', 'Matilde Villarroel', 'Benja Villarroel'
              ];
              
              const replies = await fetchCommentReplies(comment.id);
              
              return {
                id: comment.id,
                text: comment.text || '',
                timestamp: comment.timestamp,
                username: friendlyNames[index % friendlyNames.length],
                isGenerated: true,
                profilePicture: null,
                userId: null,
                replies: replies,
                replies_count: replies.length
              };
            })
          );
          
          setLastUpdate(new Date());
          return basicComments;
        } else {
          return [];
        }
      }
      
    } catch (error) {
      console.error('💥 Error al obtener comentarios:', error);
      return [];
    } finally {
      if (!isRefresh) {
        setLoadingComments(false);
      }
    }
  };

  const toggleCommentReplies = (commentId: string) => {
    const newExpanded = new Set(expandedComments);
    if (newExpanded.has(commentId)) {
      newExpanded.delete(commentId);
    } else {
      newExpanded.add(commentId);
    }
    setExpandedComments(newExpanded);
  };

  const refreshComments = async () => {
    if (selectedPost && isModalOpen) {
      console.log('🔄 Actualizando comentarios y respuestas automáticamente...');
      const updatedComments = await fetchPostComments(selectedPost.id, true);
      
      const currentCommentIds = selectedPost.comments?.map(c => c.id) || [];
      const newCommentIds = updatedComments.map(c => c.id);
      
      const hasChanges = 
        currentCommentIds.length !== newCommentIds.length ||
        !currentCommentIds.every(id => newCommentIds.includes(id));
      
      if (hasChanges) {
        console.log('🆕 Nuevos comentarios o respuestas detectados!');
        setSelectedPost({
          ...selectedPost,
          comments: updatedComments,
          comments_count: updatedComments.length
        });
        
        if (updatedComments.length > (selectedPost.comments?.length || 0)) {
          console.log('✨ Se agregaron comentarios/respuestas nuevos');
        }
      } else {
        console.log('✅ No hay cambios en los comentarios');
      }
    }
  };

  useEffect(() => {
    if (isModalOpen && selectedPost && autoRefresh) {
      console.log('⏰ Iniciando actualización automática cada 15 segundos');
      
      const interval = setInterval(() => {
        refreshComments();
      }, 15000); // Aumentado a 15 segundos por las llamadas adicionales de respuestas
      
      setRefreshInterval(interval);
      
      return () => {
        console.log('⏰ Deteniendo actualización automática');
        clearInterval(interval);
      };
    } else if (refreshInterval) {
      clearInterval(refreshInterval);
      setRefreshInterval(null);
    }
  }, [isModalOpen, selectedPost, autoRefresh]);

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedPost(null);
    setExpandedComments(new Set());
    document.body.style.overflow = 'unset';
    
    if (refreshInterval) {
      clearInterval(refreshInterval);
      setRefreshInterval(null);
    }
    
    console.log('🚪 Modal cerrada, actualizaciones automáticas detenidas');
  };

  const initializeSwiper = () => {
    setTimeout(() => {
      if (typeof window !== 'undefined' && (window as any).Swiper) {
        try {
          new (window as any).Swiper('.instagram-banner-slider', {
            slidesPerView: 1,
            spaceBetween: 30,
            loop: true,
            autoplay: {
              delay: 6000, //velocidad pasada 
              disableOnInteraction: false,
            },
            navigation: {
              nextEl: '.array-next',
              prevEl: '.array-prev',
            },
            breakpoints: {
              640: {
                slidesPerView: 2,
                spaceBetween: 20,
              },
              768: {
                slidesPerView: 3,
                spaceBetween: 40,
              },
              1024: {
                slidesPerView: 4,
                spaceBetween: 50,
              },
            },
          });
          console.log('✅ Swiper inicializado correctamente');
        } catch (error) {
          console.error('❌ Error al inicializar Swiper:', error);
        }
      }
    }, 100);
  };

  // 🔄 CARGAR POSTS SOLO SI EL TOKEN ES VÁLIDO
  useEffect(() => {
    if (tokenStatus === 'valid' || tokenStatus === 'refreshed' || !enableTokenRefresh || (!appId || !appSecret)) {
      fetchInstagramPosts();
    }
  }, [PAGE_ACCESS_TOKEN, FACEBOOK_PAGE_ID, maxPosts, tokenStatus]);

  useEffect(() => {
    if (posts.length > 0) {
      initializeSwiper();
    }
  }, [posts]);

  const openModal = async (post: InstagramPost) => {
    setSelectedPost(post);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';

    const comments = await fetchPostComments(post.id);
    setSelectedPost({
      ...post,
      comments: comments
    });
  };

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isModalOpen) {
        closeModal();
      }
    };

    if (isModalOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isModalOpen]);

  const getImageUrl = (post: InstagramPost) => {
    if (post.media_type === 'VIDEO') {
      return post.thumbnail_url || post.media_url;
    }
    return post.media_url;
  };

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCommentDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short'
    });
  };

  const getDisplayUsername = (comment: InstagramComment | InstagramCommentReply) => {
    return comment.username || 'Usuario';
  };

  // 🔄 RENDERIZADO CONDICIONAL BASADO EN ESTADO DEL TOKEN
  if (enableTokenRefresh && tokenStatus === 'checking') {
    return (
      <div className="instagram-banner fix section-padding">
        <div className="instagram-wrapper">
          <h3 className="text-center">{title}</h3>
          <div className="text-center">
            <p>🔍 Verificando estado del token...</p>
            <div className="loading-spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  if (enableTokenRefresh && (tokenStatus === 'invalid' || tokenStatus === 'error' || tokenStatus === 'refresh_failed')) {
    return (
      <div className="instagram-banner fix section-padding">
        <div className="instagram-wrapper">
          <h3 className="text-center">{title}</h3>
          <div className="text-center">
            <p className="token-error">❌ {error}</p>
            
            {enableTokenRefresh && appId && appSecret && (
              <button onClick={handleManualRefresh} className="btn-ig btn-primary">
                🔄 Renovar Token Manualmente
              </button>
            )}
            
            {tokenExpiryDate && (
              <p className="token-info">
                📅 Token expira: {tokenExpiryDate.toLocaleDateString('es-ES')}
              </p>
            )}
            
            {!appId || !appSecret ? (
              <p className="token-warning">
                ⚠️ Para habilitar la renovación automática, agrega appId y appSecret como props
              </p>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="instagram-banner fix section-padding">
        <div className="instagram-wrapper">
          <h3 className="text-center">{title}</h3>
          <div className="text-center">
            <p>Cargando posts de Instagram...</p>
            <div className="loading-spinner"></div>
            
            {tokenStatus === 'refreshed' && (
              <div className="token-success">
                <p>✅ Token renovado automáticamente</p>
                {tokenExpiryDate && (
                  <p className="token-info">
                    📅 Válido hasta: {tokenExpiryDate.toLocaleDateString('es-ES')}
                  </p>
                )}
                <p className="new-token-info">
                  🔑 Revisa la consola para ver el nuevo token
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="instagram-banner fix section-padding">
        <div className="instagram-wrapper">
          <h3 className="text-center">{title}</h3>
          <div className="text-center">
            <p className="token-error">❌ {error}</p>
            <div className="error-actions">
              <button onClick={fetchInstagramPosts} className="btn-ig btn-primary">
                Reintentar
              </button>
              {enableTokenRefresh && appId && appSecret && (
                <button onClick={handleManualRefresh} className="btn-ig btn-secondary">
                  🔄 Renovar Token
                </button>
              )}
            </div>
            
            {tokenExpiryDate && (
              <p className="token-info">
                📅 Token expira: {tokenExpiryDate.toLocaleDateString('es-ES')}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="instagram-banner fix section-padding">
        <div className="instagram-wrapper">
          <div className="section-title-area">
            <h3 className="text-center wow fadeInUp" data-wow-delay=".3s">
              {title}
            </h3>
            <div className="array-button wow fadeInUp" data-wow-delay=".5s">
              <button className="array-prev"><i className="fal fa-arrow-left"></i></button>
              <button className="array-next"><i className="fal fa-arrow-right"></i></button>
            </div>
          </div>
          
          {/* 🔄 INDICADOR DE ESTADO DEL TOKEN * /}
          {enableTokenRefresh && (tokenStatus === 'refreshed' || tokenExpiryDate) && (
            <div className="token-status-bar">
              {tokenStatus === 'refreshed' && (
                <div className="token-status-item success">
                  <span className="status-icon">🔄</span>
                  <span>Token renovado automáticamente</span>
                </div>
              )}
              
              {tokenExpiryDate && (
                <div className="token-status-item info">
                  <span className="status-icon">📅</span>
                  <span>
                    Token válido hasta: {tokenExpiryDate.toLocaleDateString('es-ES')} 
                    {isTokenExpiringSoon(tokenExpiryDate, 7) && (
                      <span className="expiry-warning"> (Expira pronto)</span>
                    )}
                  </span>
                </div>
              )} 
            </div>
          )} */}
          
          {posts.length > 0 ? (
            <>
              <div className="swiper instagram-banner-slider">
                <div className="swiper-wrapper">
                  {posts.map((post, index) => (
                    <div key={index} className="swiper-slide">
                      <div className="instagram-banner-items">
                        <div className="banner-image">
                          <img 
                            src={getImageUrl(post)} 
                            alt="insta-img" 
                            loading="lazy"
                          />
                          <button 
                            onClick={() => openModal(post)}
                            className="icon"
                            aria-label="Ver post completo"
                          >
                            <i className="fa-brands fa-instagram"></i>
                          </button>
                          {post.media_type === 'VIDEO' && (
                            <div className="video-indicator">
                              <i className="fa-solid fa-play"></i>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <hr />
              
              <script async src="//www.instagram.com/embed.js"></script>
              <blockquote 
                className="instagram-media" 
                data-instgrm-permalink={`https://www.instagram.com/${instagram}/`}
                data-instgrm-captioned
                data-instgrm-version="14"
                style={{
                  background: '#FFF',
                  border: 0,
                  borderRadius: '3px',
                  boxShadow: '0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15)',
                  margin: '1px auto',
                  maxWidth: '540px',
                  minWidth: '326px',
                  padding: 0,
                  width: 'calc(100% - 2px)'
                }}
              >
              </blockquote>
              
              <div className="text-center">
                <a 
                  href={`https://www.instagram.com/${instagram}/`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="instagram-link"
                >
                  <i className="fa-brands fa-instagram fa-1x"></i> @{instagram}
                </a>
              </div>
            </>
          ) : (
            <div className="text-center">
              <p>No se encontraron posts de Instagram</p>
              <p>Total encontrado: {posts.length}</p>
            </div>
          )}
        </div>
      </div>

      {/* MODAL CON COMENTARIOS, RESPUESTAS Y SCROLL PERSONALIZADO */}
      {isModalOpen && selectedPost && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>
              <i className="fa-solid fa-times"></i>
            </button>
            
            <div className="modal-body">
              <div className="modal-media">
                {selectedPost.media_type === 'VIDEO' ? (
                  <video 
                    controls 
                    poster={selectedPost.thumbnail_url}
                    className="modal-media-content"
                  >
                    <source src={selectedPost.media_url} type="video/mp4" />
                    Tu navegador no soporta el elemento video.
                  </video>
                ) : (
                  <img 
                    src={selectedPost.media_url} 
                    alt="Post de Instagram" 
                    className="modal-media-content"
                  />
                )}
              </div>
              
              <div className="modal-info-container">
                <div className="modal-info">
                  <div className="modal-header">
                    <div className="profile-info">
                      <i className="fa-brands fa-instagram"></i>
                      <span>@{instagram}</span>
                    </div>
                    <span className="post-date">
                      {formatDate(selectedPost.timestamp)}
                    </span>
                  </div>

                  {/* Estadísticas del post */}
                  <div className="post-stats">
                    <div className="stat-item">
                      <i className="fa-regular fa-heart"></i>
                      <span>{selectedPost.like_count || 0} me gusta</span>
                    </div>
                    <div className="stat-item">
                      <i className="fa-regular fa-comment"></i>
                      <span>{selectedPost.comments_count || 0} comentarios</span>
                    </div>
                  </div>
                  
                  {selectedPost.caption && (
                    <div className="modal-caption">
                      <div className="caption-author">
                        <strong>@{instagram}</strong>
                      </div>
                      <p>{selectedPost.caption}</p>
                    </div>
                  )}

                  {/* Sección de comentarios con respuestas */}
                  <div className="comments-section">
                    <h4 className="comments-title">
                      <i className="fa-regular fa-comment"></i>
                      Comentarios y Respuestas
                    </h4>
                    
                    {loadingComments ? (
                      <div className="comments-loading">
                        <div className="loading-spinner-small"></div>
                        <span>Cargando comentarios y respuestas...</span>
                      </div>
                    ) : selectedPost.comments && selectedPost.comments.length > 0 ? (
                      <div className="comments-list">
                        {selectedPost.comments.map((comment) => (
                          <div key={comment.id} className="comment-thread">
                            {/* Comentario principal */}
                            <div className="comment-item main-comment">
                              <div className="comment-header">
                                <div className="comment-user-info">
                                  {comment.profilePicture && (
                                    <img 
                                      src={comment.profilePicture} 
                                      alt="Avatar" 
                                      className="user-avatar"
                                    />
                                  )}
                                  <span className="comment-username">
                                    {comment.isGenerated && <span className="generated-indicator">👤</span>}
                                    {!comment.isGenerated && <span className="real-user-indicator">✓</span>}
                                    {getDisplayUsername(comment)}
                                  </span>
                                </div>
                                <div className="comment-meta">
                                  <span className="comment-date">{formatCommentDate(comment.timestamp)}</span>
                                  {comment.replies && comment.replies.length > 0 && (
                                    <button 
                                      className="replies-toggle"
                                      onClick={() => toggleCommentReplies(comment.id)}
                                    >
                                      <i className={`fa-solid ${expandedComments.has(comment.id) ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                                      {comment.replies.length} {comment.replies.length === 1 ? 'respuesta' : 'respuestas'}
                                    </button>
                                  )}
                                </div>
                              </div>
                              <p className="comment-text">{comment.text}</p>
                            </div>
                            
                            {/* Respuestas del comentario */}
                            {comment.replies && comment.replies.length > 0 && expandedComments.has(comment.id) && (
                              <div className="replies-container">
                                {comment.replies.map((reply) => (
                                  <div key={reply.id} className="comment-item reply-comment">
                                    <div className="reply-indicator">
                                      <i className="fa-solid fa-reply"></i>
                                    </div>
                                    <div className="reply-content">
                                      <div className="comment-header">
                                        <div className="comment-user-info">
                                          {reply.profilePicture && (
                                            <img 
                                              src={reply.profilePicture} 
                                              alt="Avatar" 
                                              className="user-avatar small"
                                            />
                                          )}
                                          <span className="comment-username">
                                            {reply.isGenerated && <span className="generated-indicator">👤</span>}
                                            {!reply.isGenerated && <span className="real-user-indicator">✓</span>}
                                            {getDisplayUsername(reply)}
                                          </span>
                                        </div>
                                        <span className="comment-date">{formatCommentDate(reply.timestamp)}</span>
                                      </div>
                                      <p className="comment-text">{reply.text}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                        
                        {/* Información sobre tipos de usuarios y actualización */}
                        <div className="comments-info">
                          <div className="comments-stats">
                            <div className="info-item">
                              <span className="real-user-indicator">✓</span>
                              <span>Usuarios reales</span>
                            </div>
                            <div className="info-item">
                              <span className="generated-indicator">👤</span>  
                              <span>Usuarios anónimos</span>
                            </div>
                          </div>
                          
                          <div className="refresh-controls">
                            <div className="auto-refresh-toggle">
                              <input 
                                type="checkbox" 
                                id="autoRefresh" 
                                checked={autoRefresh}
                                onChange={(e) => setAutoRefresh(e.target.checked)}
                                className="refresh-checkbox"
                              />
                              <label htmlFor="autoRefresh" className="refresh-label">
                                🔄 Auto-actualizar (incluye respuestas)
                              </label>
                            </div>
                            
                            <button 
                              onClick={() => refreshComments()}
                              className="manual-refresh-btn"
                              disabled={loadingComments}
                            >
                              🔄 Actualizar comentarios y respuestas
                            </button>
                            
                            {lastUpdate && (
                              <div className="last-update">
                                Última actualización: {lastUpdate.toLocaleTimeString('es-ES', { 
                                  hour: '2-digit', 
                                  minute: '2-digit',
                                  second: '2-digit'
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="no-comments">
                        <i className="fa-regular fa-comment-slash"></i>
                        <span>No hay comentarios aún</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="modal-actions">
                    <a 
                      href={selectedPost.permalink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="theme-btn btn-ig btn-instagram"
                    >
                      <i className="fa-brands fa-instagram mx-2"></i>
                      Ver en Instagram
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default KidsaInstagram;